import React, { useState, useRef, useEffect } from 'react';
import './CustomSelect.css';

const CustomSelect = ({ 
  options, 
  value, 
  onChange, 
  placeholder, 
  name,
  required = false,
  creatable = false,
  openUp = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const selectRef = useRef(null);
  const searchInputRef = useRef(null);

  // Normalize options - support both strings and objects
  const safeOptions = Array.isArray(options) ? options : [];
  
  const normalizedOptions = safeOptions.map(opt => {
    if (typeof opt === 'string') {
      return { name: opt, avatar_url: null };
    }
    if (opt && typeof opt === 'object') {
      return opt;
    }
    return { name: String(opt), avatar_url: null };
  });

  // Get current selected option
  const getSelectedOption = () => {
    if (!value) return null;
    const val = typeof value === 'string' ? value : (value.name || value);
    return normalizedOptions.find(opt => {
      const optValue = typeof opt === 'string' ? opt : opt.name;
      return optValue === val;
    });
  };

  const selectedOption = getSelectedOption();

  // Filter options based on search
  const filteredOptions = normalizedOptions.filter(opt => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    const name = typeof opt === 'string' ? opt : opt.name;
    return name.toLowerCase().includes(searchLower);
  });

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  };

  const handleSelect = (option) => {
    const optionValue = typeof option === 'string' ? option : option.name;
    onChange({ target: { name, value: optionValue } });
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleCreateNew = () => {
    if (searchQuery.trim()) {
      onChange({ target: { name, value: searchQuery.trim() } });
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && creatable && searchQuery.trim() && filteredOptions.length === 0) {
      e.preventDefault();
      handleCreateNew();
    }
  };

  const handleClickOutside = (event) => {
    if (selectRef.current && !selectRef.current.contains(event.target)) {
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const displayValue = selectedOption 
    ? (typeof selectedOption === 'string' ? selectedOption : selectedOption.name)
    : value || '';

  return (
    <div className="custom-select" ref={selectRef}>
      <div 
        className={`custom-select__trigger ${isOpen ? 'open' : ''}`}
        onClick={handleToggle}
      >
        <div className="custom-select__value-wrapper">
          {selectedOption && typeof selectedOption === 'object' && selectedOption.avatar_url && (
            <img 
              src={selectedOption.avatar_url} 
              alt={selectedOption.name}
              className="custom-select__avatar"
            />
          )}
          <span className={`custom-select__value ${!displayValue ? 'placeholder' : ''}`}>
            {displayValue || placeholder}
          </span>
        </div>
        <span className="custom-select__arrow">▼</span>
      </div>
      
      {isOpen && (
        <div className={`custom-select__dropdown ${openUp ? 'open-up' : ''}`}>
          {!openUp && (
            <div className="custom-select__search">
              <input
                ref={searchInputRef}
                type="text"
                className="custom-select__search-input"
                placeholder={creatable ? "Поиск или введите новое значение..." : "Поиск..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          <div className="custom-select__options">
            {filteredOptions.length > 0 ? (
              <>
                {filteredOptions.map((option, index) => {
                  const optionName = typeof option === 'string' ? option : option.name;
                  const optionAvatar = typeof option === 'object' ? option.avatar_url : null;
                  const isSelected = displayValue === optionName;
                  
                  return (
                    <div
                      key={index}
                      className={`custom-select__option ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleSelect(option)}
                    >
                      {optionAvatar && (
                        <img 
                          src={optionAvatar} 
                          alt={optionName}
                          className="custom-select__option-avatar"
                        />
                      )}
                      <span className="custom-select__option-text">{optionName}</span>
                    </div>
                  );
                })}
                {creatable && searchQuery.trim() && !filteredOptions.some(opt => {
                  const optName = typeof opt === 'string' ? opt : opt.name;
                  return optName.toLowerCase() === searchQuery.trim().toLowerCase();
                }) && (
                  <div
                    className="custom-select__option custom-select__option--create"
                    onClick={handleCreateNew}
                  >
                    <span className="custom-select__option-text">
                      Создать: <strong>{searchQuery.trim()}</strong>
                    </span>
                  </div>
                )}
              </>
            ) : (
              <>
                {creatable && searchQuery.trim() ? (
                  <div
                    className="custom-select__option custom-select__option--create"
                    onClick={handleCreateNew}
                  >
                    <span className="custom-select__option-text">
                      Создать: <strong>{searchQuery.trim()}</strong>
                    </span>
                  </div>
                ) : (
                  <div className="custom-select__option custom-select__option--no-results">
                    Ничего не найдено
                  </div>
                )}
              </>
            )}
          </div>
          {openUp && (
            <div className="custom-select__search">
              <input
                ref={searchInputRef}
                type="text"
                className="custom-select__search-input"
                placeholder={creatable ? "Поиск или введите новое значение..." : "Поиск..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
